import { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { SearchInput } from '@/shared/components/ui/search-input'
import { LucideIconByName, useLucideIconNames } from '@/shared/components/ui/lucide-icon-by-name'

const ROWS_PER_PAGE = 8
const COLS = 7
const ICONS_PER_PAGE = ROWS_PER_PAGE * COLS

function formatLabel(name: string): string {
  return name
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function IconGrid({
  search,
  value,
  onSelect,
}: {
  search: string
  value: string
  onSelect: (key: string) => void
}) {
  const [page, setPage] = useState(0)
  const [prevSearch, setPrevSearch] = useState(search)
  // Names come from the lazily-loaded icon set, so the grid is empty for the
  // first tick after the picker opens.
  const allNames = useLucideIconNames()
  const { t } = useTranslation()

  const filtered = useMemo(() => {
    if (!search) return allNames
    const q = search.toLowerCase()
    return allNames.filter((name) => name.includes(q))
  }, [search, allNames])

  if (prevSearch !== search) {
    setPrevSearch(search)
    if (page !== 0) setPage(0)
  }

  const totalPages = Math.ceil(filtered.length / ICONS_PER_PAGE)
  const visible = filtered.slice(page * ICONS_PER_PAGE, (page + 1) * ICONS_PER_PAGE)

  if (allNames.length === 0) {
    return (
      <p className="py-6 text-center text-[11px] text-[var(--ink-3)]">{t('common.loading')}</p>
    )
  }

  if (filtered.length === 0) {
    return (
      <p className="py-6 text-center text-[11px] text-[var(--ink-3)]">
        No icons match &ldquo;{search}&rdquo;
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="grid grid-cols-7 gap-1">
        {visible.map((name) => {
          const selected = value === name
          return (
            <Button
              key={name}
              variant="ghost"
              size="sm"
              title={formatLabel(name)}
              onClick={() => onSelect(selected ? '' : name)}
              className={cn(
                'flex h-auto cursor-pointer flex-col items-center gap-1 rounded-[6px] border-[0.5px] px-0.5 py-2 transition-all duration-150',
                selected
                  ? 'border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]'
                  : 'border-transparent text-[var(--ink-3)] hover:border-[var(--rule)] hover:bg-[var(--bg-light)]',
              )}
            >
              <LucideIconByName
                name={name}
                size={18}
                strokeWidth={1.5}
                className={cn(selected ? 'text-[var(--green)]' : 'text-[var(--ink-3)]')}
              />
              <span className="max-w-full truncate text-[8px] leading-none">{formatLabel(name)}</span>
            </Button>
          )
        })}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-1 pt-1">
          <span className="text-[10px] text-[var(--ink-3)]">
            {filtered.length} icons
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon-xs"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="h-5 w-5 text-[10px]"
            >
              &lsaquo;
            </Button>
            <span className="text-[10px] text-[var(--ink-3)]">
              {page + 1}/{totalPages}
            </span>
            <Button
              variant="ghost"
              size="icon-xs"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="h-5 w-5 text-[10px]"
            >
              &rsaquo;
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export function LucideIconPicker({
  value,
  onChange,
  mode = 'floating',
  label,
}: {
  value: string
  onChange: (key: string) => void
  mode?: 'embed' | 'floating'
  label?: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 })

  const handleSelect = useCallback(
    (key: string) => {
      onChange(key)
      if (mode === 'floating') setOpen(false)
    },
    [onChange, mode],
  )

  useLayoutEffect(() => {
    if (mode !== 'floating' || !open || !triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    setDropdownPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 340),
    })
  }, [mode, open])

  useEffect(() => {
    if (mode !== 'floating' || !open) return
    function handleClick(e: MouseEvent) {
      const target = e.target as Node
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [mode, open])

  const pickerContent = (
    <div className="flex flex-col gap-2">
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder="Search icons…"
        className="h-8 text-[12px]"
      />
      <div className="max-h-[280px] overflow-y-auto overscroll-contain">
        <IconGrid search={search} value={value} onSelect={handleSelect} />
      </div>
    </div>
  )

  if (mode === 'embed') {
    return (
      <div className="flex flex-col gap-1.5">
        {label && <Label className="clara-section-label mb-0 block">{label}</Label>}
        <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-2.5">
          {pickerContent}
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative flex flex-col gap-1.5">
      {label && <Label className="clara-section-label mb-0 block">{label}</Label>}
      <Button
        ref={triggerRef}
        variant="outline"
        onClick={() => {
          setOpen(!open)
          setSearch('')
        }}
        className={cn(
          'flex h-9 w-full cursor-pointer items-center justify-start gap-2.5 rounded-[4px] border-[0.5px] bg-[var(--bg)] px-3 text-left text-[13px] font-normal shadow-none transition-colors duration-150',
          open ? 'border-[var(--green)]' : 'border-[var(--rule)]',
        )}
      >
        {value ? (
          <>
            <LucideIconByName
              name={value}
              size={16}
              strokeWidth={1.5}
              className="shrink-0 text-[var(--ink-2)]"
            />
            <span className="text-[var(--ink)]">{formatLabel(value)}</span>
          </>
        ) : (
          <span className="text-[var(--ink-3)]">Choose an icon…</span>
        )}
        {value && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation()
              onChange('')
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation()
                onChange('')
              }
            }}
            className="ml-auto flex h-5 w-5 items-center justify-center rounded-[4px] text-[var(--ink-3)] hover:bg-[var(--bg-light)] hover:text-[var(--ink)]"
          >
            <X size={14} />
          </span>
        )}
      </Button>
      {open && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-50 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-2.5 shadow-[0_4px_12px_var(--overlay-light)]"
          style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
        >
          {pickerContent}
        </div>,
        document.body,
      )}
    </div>
  )
}
